// Import necessary dependencies from React and Material-UI
import React from "react";
import "./List.css";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import TrendingDownRoundedIcon from "@mui/icons-material/TrendingDownRounded";
import { Tooltip } from "@mui/material";
import { Link } from "react-router-dom";
import StarsIcon from '@mui/icons-material/Stars';
import starredCoin from "../../../functions/starredCoin";
import { useDispatch } from "react-redux";
import { motion } from "framer-motion";

/**
 * List component represents a list item displaying information about a cryptocurrency.
 * @param {object} coin - Information about the cryptocurrency.
 * @param {number} index - Index of the list item.
 * @returns {JSX.Element} - List component.
 */
function List({ coin, index }) {
  // Access the dispatch function from the Redux store
  const dispatch = useDispatch();

  // Styling object based on the price change percentage
  const style = {
    color: coin.price_change_percentage_24h < 0 ? "red" : "green",
  }

  // Function to handle starring/unstarring a coin
  const handleStarredItem = () => {
    starredCoin(coin, dispatch);
  }

  return (
    // Motion div for animation effects
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index / 10 }}
      className="rel-obj11"
    >
      {/* Icon for starring/unstarring a coin */}
      <div className="icon" onClick={handleStarredItem}>
        {coin.starred ?
          <Tooltip title="Unstar" placement="bottom-start">
            <StarsIcon color="primary" sx={{ fontSize: 30 }} />
          </Tooltip>
          :
          <Tooltip title="Star" placement="bottom-start">
            <StarsIcon color="" sx={{ fontSize: 30 }} />
          </Tooltip>
        }
      </div>

      {/* Link to the detailed page of the cryptocurrency */}
      <Link to={`/coin/${coin.id}`}>
        {/* Container with flex layout for displaying cryptocurrency information */}
        <div className="d-flex">
          {/* Container for the cryptocurrency logo */}
          <div className="f-width">
            <Tooltip title="Icon" placement="bottom-start">
              <div>
                <img src={coin.image} alt={coin.id} className="coin-logo" />
              </div>
            </Tooltip>
          </div>
          {/* Container for the cryptocurrency name and symbol */}
          <div className="f-width">
            <Tooltip title="Coin info" placement="bottom-start">
              <div>
                <div>
                  <p style={{ margin: 0 }}>{coin.name}</p>
                  <p style={{ margin: 0 }}>{coin.symbol}</p>
                </div>
              </div>
            </Tooltip>
          </div>
          {/* Container for the price change percentage */}
          <div className="f-width">
            <Tooltip title="Price change in 24Hrs" placement="bottom-start">
              <p style={style}>{coin.price_change_percentage_24h.toFixed(2)}%</p>
            </Tooltip>
          </div>
          {/* Container for the trend indicator */}
          <div className="f-width">
            <Tooltip title="Insight in 24Hrs" placement="bottom-start">
              {coin.price_change_percentage_24h > 0 ?
                <p><TrendingUpRoundedIcon style={style} /></p> :
                <p><TrendingDownRoundedIcon style={style} /></p>}
            </Tooltip>
          </div>
          {/* Container for the current price */}
          <div className="f-width">
            <Tooltip title="Current price">
              <p style={style}>${coin.current_price.toLocaleString()}</p>
            </Tooltip>
          </div>
          {/* Container for the total volume */}
          <div className="f-width">
            <Tooltip title="Total volume" placement="bottom-start">
              <p>{coin.total_volume.toLocaleString()}</p>
            </Tooltip>
          </div>
          {/* Container for the market cap */}
          <div className="f-width">
            <Tooltip title="Market cap">
              <p>${coin.market_cap.toLocaleString()}</p>
            </Tooltip>
          </div>
          {/* Additional empty container */}
          <div className="f-width"></div>
        </div>
      </Link>
    </motion.div>
  );
}

// Export the List component as the default export of the module
export default List;
